import React, { createContext, useState, useEffect } from 'react';
import { User, AuthError, Session } from '@supabase/supabase-js';
import { supabase, testSupabaseConnection, clearAuthCache } from '../lib/supabase';
import { products } from '../stripe-config';
import { logger } from '../lib/logger';
import { identifyUser, resetAnalytics, track } from '../lib/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ user: User; session: Session }>;
  signInWithGoogle: () => Promise<void>;
  signUp: (email: string, password: string, name?: string, planId?: string, source?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signIn: async () => { throw new Error('Not implemented'); },
  signInWithGoogle: async () => {},
  signUp: async () => {},
  signOut: async () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      logger.debug('Initial auth session loaded:', session ? 'active' : 'none');

      // If no session, clear any stale auth data
      if (!session) {
        clearAuthCache();
      }

      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        identifyUser(session.user.id, { email: session.user.email });
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      logger.debug('Auth state changed:', _event, 'User ID:', session?.user?.id);

      // If auth error or sign out, clear auth cache
      if (_event === 'SIGNED_OUT' || _event === 'TOKEN_REFRESHED' || _event === 'USER_UPDATED') {
        logger.debug('Auth event requires cache clearing:', _event);
        clearAuthCache();
      }

      if (_event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user.id, { email: session.user.email });
      }
      if (_event === 'SIGNED_OUT') {
        resetAnalytics();
      }

      setSession(session);
      // Stabilize user reference: only update if the user identity changed.
      // TOKEN_REFRESHED creates a new user object with the same id - keeping the
      // old reference prevents cascading re-renders and refetches downstream.
      setUser(prev => {
        const newUser = session?.user ?? null;
        if (prev?.id === newUser?.id) return prev;
        return newUser;
      });
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      logger.debug(`Signing in with email: ${email}`);
      
      const { error, data } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) {
        logger.error('Sign in error from Supabase:', error);
        throw error;
      }
      
      // Explicit validation of auth response data
      if (!data) {
        logger.error('Sign in failed: No data returned from auth service');
        throw new Error('Authentication failed: No response data');
      }
      
      if (!data.user) {
        logger.error('Sign in failed: No user object in response data', data);
        throw new Error('Authentication failed: User information missing');
      }
      
      if (!data.session) {
        logger.error('Sign in failed: No session object in response data', data);
        throw new Error('Authentication failed: Session information missing');
      }
      
      logger.debug('Sign in successful, user:', data.user?.id);
      
      // Update context state immediately
      setSession(data.session);
      setUser(data.user);
      
      return { user: data.user, session: data.session };
    } catch (error) {
      const authError = error as AuthError;
      logger.error('Sign in error:', authError);
      throw authError;
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) throw error;
    } catch (error) {
      const authError = error as AuthError;
      logger.error('Google sign in error:', authError);
      throw authError;
    }
  };

  const signUp = async (email: string, password: string, name?: string, planId?: string, source?: string) => {
    try {
      // Test database connection before proceeding
      let isConnected = false;
      try {
        isConnected = await testSupabaseConnection();
      } catch (connErr) {
        logger.error('Connection test failed:', connErr);
        // Continue anyway, as this might be a temporary issue
      }

      // Default to FREE plan if none specified
      const freePlanId = products.free.priceId;
      const selectedPlanId = planId || freePlanId;

      logger.debug(`Signing up user with email: ${email}, plan: ${selectedPlanId} (${selectedPlanId === freePlanId ? 'FREE' : 'PAID'})`);

      const metadata: Record<string, any> = {
        full_name: name || '',
        selected_plan: selectedPlanId,
        onboarding_completed: false,
      };
      if (source) {
        metadata.signup_source = source;
      }

      // Sign up the user with Supabase Auth
      const { error: signUpError, data } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: `${window.location.origin}/dashboard`
        },
      });
      
      if (signUpError) {
        logger.error('Auth signup error:', signUpError);
        throw signUpError;
      }
      
      if (!data.user) {
        logger.error('User creation failed - no user data returned');
        throw new Error('User creation failed');
      }
      
      logger.debug(`User signed up successfully with ID: ${data.user.id}, redirecting to onboarding`);

      identifyUser(data.user.id, { email: data.user.email });
      track('prealgo_signup_completed', {
        plan_id: selectedPlanId,
        is_paid: selectedPlanId !== freePlanId,
        signup_source: source ?? null,
      });
      
      // Manually ensure profile is created
      try {
        const { error: profileError } = await supabase
          .from('profiles')
          .upsert({
            id: data.user.id,
            full_name: name || '',
            onboarding_completed: false,
            plan_id: selectedPlanId
          });
          
        if (profileError) {
          logger.warn('Error creating profile:', profileError);
        }
      } catch (profileErr) {
        logger.warn('Error creating profile:', profileErr);
      }
      
    } catch (error) {
      logger.error('Sign up error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      logger.debug('Signing out user');

      // Clear auth cache before signing out
      clearAuthCache();
      
      // First clear the local state
      setUser(null);
      setSession(null);
      
      // Then attempt to sign out from Supabase
      await supabase.auth.signOut();
      
      logger.debug('Sign out successful');
    } catch (error) {
      logger.error('Sign out error:', error);
      // Don't throw the error to prevent blocking UI
    }
  };

  return (
    <AuthContext.Provider 
      value={{ 
        user,
        session,
        loading, 
        signIn,
        signInWithGoogle, 
        signUp, 
        signOut 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};